// Sounds of the back room's props: the fireplace roaring when clicked (a
// rushing whoosh with a low rumble and crackles) and a free drink being
// grabbed from the cart (a can cracked open, a pour and a fizz). They plug
// into the Sound system in audio.ts, so the volume settings apply and a
// recorded file at /sounds/<name>.mp3 (e.g. sounds/roar.mp3) replaces the synth.
//
// This module must not import audio.ts at runtime (audio.ts imports it).

export const ROOM_SOUNDS = ["roar", "pour"] as const;
export type RoomSound = (typeof ROOM_SOUNDS)[number];

/** Base loudness of each room sound before the volume settings (0..1). */
export const ROOM_SOUND_LEVEL: Record<RoomSound, number> = {
  roar: 0.5,
  pour: 0.4,
};

type Synth = (ctx: AudioContext, out: AudioNode, t0: number) => void;

function noiseSource(ctx: AudioContext, seconds: number): AudioBufferSourceNode {
  const length = Math.ceil(ctx.sampleRate * seconds);
  const buffer = ctx.createBuffer(1, length, ctx.sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < length; i++) data[i] = Math.random() * 2 - 1;
  const src = ctx.createBufferSource();
  src.buffer = buffer;
  return src;
}

/** Filtered noise with a gain envelope given as [time offset (s), level] points. */
function shapedNoise(ctx: AudioContext, out: AudioNode, t0: number, seconds: number, type: BiquadFilterType, freq: [number, number][], q: number, env: [number, number][]): void {
  const src = noiseSource(ctx, seconds);
  const f = ctx.createBiquadFilter();
  f.type = type;
  f.Q.value = q;
  f.frequency.setValueAtTime(freq[0]![1], t0);
  for (const [dt, hz] of freq.slice(1)) f.frequency.exponentialRampToValueAtTime(hz, t0 + dt);
  const g = ctx.createGain();
  g.gain.setValueAtTime(0.0001, t0);
  for (const [dt, v] of env) g.gain.exponentialRampToValueAtTime(Math.max(0.0001, v), t0 + dt);
  src.connect(f).connect(g).connect(out);
  src.start(t0);
  src.stop(t0 + seconds + 0.02);
}

/** A short click of noise (a crackle, a pop, a bubble). */
function tick(ctx: AudioContext, out: AudioNode, t: number, ms: number, freq: number, level: number): void {
  shapedNoise(ctx, out, t, ms / 1000, "bandpass", [[0, freq]], 1.2, [
    [0.002, level],
    [ms / 1000, 0.0001],
  ]);
}

export const ROOM_SYNTHS: Record<RoomSound, Synth> = {
  // The fire roars up: a big rising whoosh of air, a deep rumble under it,
  // and dry crackles and pops all the way through, dying down after ~2.5 s.
  roar: (ctx, out, t0) => {
    shapedNoise(ctx, out, t0, 2.8, "bandpass", [
      [0, 260],
      [0.35, 1300],
      [1.4, 900],
      [2.7, 300],
    ], 0.7, [
      [0.3, 0.9],
      [1.4, 0.7],
      [2.7, 0.0001],
    ]);
    shapedNoise(ctx, out, t0, 2.8, "lowpass", [
      [0, 120],
      [0.4, 220],
      [2.7, 90],
    ], 0.9, [
      [0.25, 1],
      [1.5, 0.75],
      [2.7, 0.0001],
    ]);
    const rumble = ctx.createOscillator();
    const rg = ctx.createGain();
    rumble.type = "sine";
    rumble.frequency.setValueAtTime(48, t0);
    rumble.frequency.linearRampToValueAtTime(62, t0 + 0.4);
    rumble.frequency.linearRampToValueAtTime(42, t0 + 2.6);
    rg.gain.setValueAtTime(0.0001, t0);
    rg.gain.exponentialRampToValueAtTime(0.5, t0 + 0.3);
    rg.gain.exponentialRampToValueAtTime(0.0001, t0 + 2.6);
    rumble.connect(rg).connect(out);
    rumble.start(t0);
    rumble.stop(t0 + 2.7);
    for (let i = 0; i < 26; i++) {
      const at = t0 + 0.05 + Math.random() * Math.random() * 2.4;
      tick(ctx, out, at, 8 + Math.random() * 22, 1500 + Math.random() * 4500, 0.35 + Math.random() * 0.6);
    }
  },

  // A free fizzy drink: the can cracks open (a pop and a hiss), it pours with
  // a gurgle, then fizzes in the glass.
  pour: (ctx, out, t0) => {
    tick(ctx, out, t0, 18, 2600, 1);
    shapedNoise(ctx, out, t0 + 0.01, 0.35, "highpass", [[0, 5000]], 0.7, [
      [0.02, 0.6],
      [0.35, 0.0001],
    ]);
    // The pour: a gurgling band of noise wobbling in pitch.
    const p0 = t0 + 0.35;
    shapedNoise(ctx, out, p0, 1.1, "bandpass", [
      [0, 500],
      [0.3, 750],
      [0.6, 600],
      [1.0, 900],
    ], 2.5, [
      [0.08, 0.7],
      [0.9, 0.6],
      [1.1, 0.0001],
    ]);
    for (let i = 0; i < 9; i++) {
      const at = p0 + 0.05 + i * 0.11 + Math.random() * 0.04;
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.type = "sine";
      const f = 380 + Math.random() * 260;
      o.frequency.setValueAtTime(f, at);
      o.frequency.exponentialRampToValueAtTime(f * 1.8, at + 0.06);
      g.gain.setValueAtTime(0.0001, at);
      g.gain.exponentialRampToValueAtTime(0.25, at + 0.01);
      g.gain.exponentialRampToValueAtTime(0.0001, at + 0.07);
      o.connect(g).connect(out);
      o.start(at);
      o.stop(at + 0.08);
    }
    // The fizz: a soft hiss full of tiny bubbles popping.
    const f0 = t0 + 1.2;
    shapedNoise(ctx, out, f0, 1.4, "highpass", [[0, 6000]], 0.7, [
      [0.1, 0.22],
      [1.4, 0.0001],
    ]);
    for (let i = 0; i < 30; i++) tick(ctx, out, f0 + Math.random() * 1.3, 4 + Math.random() * 5, 4000 + Math.random() * 5000, 0.15 + Math.random() * 0.25);
  },
};
